import { Button, PageContainer, PageHeader, SearchInput, Tabs, TabsList, TabsTab } from '@ccp/ui';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import { useTicketByNumber, useTicketsByStatus } from '../../features/tickets/api.js';
import { TicketList } from '../../features/tickets/components/ticket-list.js';

/**
 * What the address bar carries, validated like any other boundary.
 *
 * Both are in the URL rather than in a store so that one person can send another the list they are
 * looking at — "the open ones" and "#1042" are both things people say to each other. A status
 * nobody has falls back to `open` rather than refusing: a mistyped link should show a list, not an
 * error page. A number that is not a number is dropped for the same reason.
 */
const searchSchema = z.object({
  status: z.enum(['open', 'pending', 'resolved', 'closed']).catch('open').default('open'),
  number: z.coerce.number().int().positive().optional().catch(undefined),
});

export const Route = createFileRoute('/tickets/')({
  validateSearch: searchSchema,
  component: TicketsScreen,
});

/**
 * The tickets area: what is open, and the one somebody has the number of.
 *
 * There is no "all tickets" and there should not be: the API refuses it, because every ticket a
 * tenant has is a report rather than a screen. What this offers instead is the two questions
 * people actually ask — which are in this status, and where is number 1042.
 */
function TicketsScreen() {
  const { t } = useTranslation('tickets');
  const navigate = useNavigate();
  const { status, number } = Route.useSearch();
  const byStatus = useTicketsByStatus(status);
  const byNumber = useTicketByNumber(number);
  const [typed, setTyped] = useState(number === undefined ? '' : String(number));

  const searching = number !== undefined;
  const found = byNumber.data;
  const tickets = searching ? (found === undefined ? [] : [found]) : (byStatus.data ?? []);

  const open = (ticketId: string) =>
    void navigate({ to: '/tickets/$ticketId', params: { ticketId } });

  return (
    <PageContainer width="full">
      <PageHeader title={t('list.heading')} />

      {/* One toolbar: which tickets, on the left, and finding one by number, on the right. The
          search was a labelled field beside the heading whose placeholder, "1042", read as a
          number somebody had already typed (M5.5-01). */}
      <div className="flex flex-wrap items-end justify-between gap-(--space-sm)">
        <Tabs
          value={status}
          onValueChange={(value) =>
            void navigate({
              to: '/tickets',
              search: { status: String(value) as typeof status },
            })
          }
          className="min-w-0"
        >
          <TabsList>
            {(['open', 'pending', 'resolved', 'closed'] as const).map((option) => (
              <TabsTab key={option} value={option}>
                {t(`status.${option}`)}
              </TabsTab>
            ))}
          </TabsList>
        </Tabs>

        <search>
          <form
            className="flex items-center gap-(--space-xs)"
            onSubmit={(event) => {
              event.preventDefault();
              const asked = Number(typed.replace(/[^0-9]/g, ''));
              void navigate({
                to: '/tickets',
                search: {
                  status,
                  ...(Number.isInteger(asked) && asked > 0 ? { number: asked } : {}),
                },
              });
            }}
          >
            <SearchInput
              label={t('list.findByNumber')}
              inputMode="numeric"
              value={typed}
              placeholder={t('list.numberPlaceholder')}
              onChange={(event) => setTyped(event.target.value)}
              className="w-(--width-menu)"
            />
            <Button type="submit" variant="secondary">
              {t('list.find')}
            </Button>
            {searching ? (
              <Button
                variant="ghost"
                onClick={() => {
                  setTyped('');
                  void navigate({ to: '/tickets', search: { status } });
                }}
              >
                {t('list.clear')}
              </Button>
            ) : null}
          </form>
        </search>
      </div>

      {searching && !byNumber.isPending && found === undefined ? (
        <p role="status" className="text-(length:--font-size-body) text-(--color-text-secondary)">
          {t('list.noSuchNumber', { number })}
        </p>
      ) : null}

      <TicketList
        tickets={tickets}
        loading={searching ? byNumber.isPending : byStatus.isPending}
        onOpen={(ticket) => open(ticket.id)}
      />
    </PageContainer>
  );
}
